/**
 * advance-ai-targeting.ts — threat-weighted target evaluation helpers
 * (v4M8.2a) per `docs/AI.md §5.1`.
 *
 * Pure-function module. No state mutation, no RNG draws. Safe to call
 * any number of times per tick.
 *
 * v4M8.2a status: helpers land but are NOT yet consumed by dispatch.
 * The legacy `pickCombatJumpDestination` in `advance-ai.ts` remains the
 * active path. v4M8.2b adds the new dispatch entry point using these
 * helpers; v4M8.2c adds an A* pathing extension with `pathFuelWeight`.
 * The consumer flip lives in a later v4M8.x checkpoint after .2a/.2b/
 * .2c land + bake.
 *
 * Composition formula (AI.md §5.1):
 *
 *   targetScore(target) = strategicValue(target) × strategicWeight
 *                         − dangerFactor × enemyStrength(target) / ownReadiness
 *
 * Where the four inputs each compose from primitive ship + planet
 * signals. The per-archetype tunables (`strategicWeight`, `dangerFactor`,
 * `minReadinessRatio`) flow in from `composeEffectiveBehaviour`; this
 * module stays free of behaviour-composition concerns.
 */

import type {
  EffectiveBehaviour,
  Empire,
  GameState,
  Planet,
  ShipInstance,
  SystemId,
  WeaponDef,
} from '../state/types/index';
import { EMPIRE_NEUTRAL_ID } from '../state/types/index';
import { firstStepTowards, firstStepTowardsWeighted } from './pathfinding';
import { effectiveMaxHull, effectiveMaxShields } from './research-effects';
import { areTeammates } from './teams';
import { teammateClaimCountForSystem } from './advance-ai-team-claims';
import { aiHazardPenaltyForSystem } from './balance-environment';

// ---------------------------------------------------------------------------
// Damage-potential primitives
// ---------------------------------------------------------------------------

/**
 * Per-weapon damage potential — `weapon.damage × weapon.accuracy`.
 *
 * Captures the expected damage of one fired round before defensive
 * mitigations (shields / armor / damage-type modifiers). Used as the
 * primitive that aggregates into `shipDamagePotential` and from there
 * into `enemyStrength` / `ownReadiness`.
 *
 * `accuracy` is the pre-roll probability; weapons are uniformly rolled
 * against a [0, 1) draw at fire time, so multiplying damage by accuracy
 * gives the unbiased expected per-round damage.
 */
export function damagePotential(weapon: WeaponDef): number {
  return weapon.damage * weapon.accuracy;
}

/**
 * Sum of `damagePotential` across all of a ship's equipped weapon slots,
 * resolved through the ship's race × chassis template's `weaponLoadout`.
 *
 * Missing weapons (xlsx authoring gap, retired weapon name) contribute
 * 0 and don't throw — the threat-eval pipeline must be robust to
 * mid-game catalogue inconsistencies.
 *
 * Does NOT consider current ammo or cooldown: the helper measures the
 * ship's *theoretical* firepower per fire-event, not its instantaneous
 * delivery rate. The `0.7 × hull% + 0.3 × shield%` HP weighting at the
 * `enemyStrength` aggregation layer handles "degraded ship contributes
 * less"; cooldown gating is too fine-grained for the strategic-scoring
 * layer.
 */
export function shipDamagePotential(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
): number {
  const template = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!template) return 0;
  let total = 0;
  for (const slot of template.weaponLoadout) {
    const weapon = state.weapons.get(slot.weapon);
    if (!weapon) continue;
    total += damagePotential(weapon) * slot.count;
  }
  return total;
}

/**
 * HP-weighted health factor for a ship per AI.md §5.1:
 *
 *   factor = 0.7 × (hull / maxHull) + 0.3 × (shield / maxShield)
 *
 * Bounded to [0, 1]. Used as the multiplier on `shipDamagePotential`
 * when summing into `enemyStrength` — a damaged ship contributes less
 * to the perceived threat because its damage delivery is curtailed
 * before it expires.
 *
 * `ShipInstance` carries `currentHull` + `currentShields` directly but
 * max values are template-derived via `effectiveMaxHull` /
 * `effectiveMaxShields` (research-effects.ts). The helper therefore
 * needs state + empire context to resolve the template and apply
 * empire-side research multipliers.
 *
 * Falls back to `1.0` (full strength) when the template can't be
 * resolved — better to err toward overestimating an enemy than to
 * silently produce 0 strength on a catalogue gap. Shieldless chassis
 * (maxShield === 0) collapse the shield term to 0 and the ship
 * effectively maxes out at 0.7 even at full hull.
 */
export function shipHealthFactor(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
): number {
  const template = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!template) return 1.0;
  const maxHull = effectiveMaxHull(state, empire, template);
  const maxShields = effectiveMaxShields(state, empire, template);
  const hullFrac =
    maxHull > 0 ? Math.max(0, Math.min(1, ship.currentHull / maxHull)) : 0;
  const shieldFrac =
    maxShields > 0
      ? Math.max(0, Math.min(1, ship.currentShields / maxShields))
      : 0;
  return 0.7 * hullFrac + 0.3 * shieldFrac;
}

// ---------------------------------------------------------------------------
// Per-system strength aggregations
// ---------------------------------------------------------------------------

/**
 * Sum of visible enemy ships' `damagePotential × shipHealthFactor` in a
 * candidate target system, from `empire`'s perspective.
 *
 * "Visible" follows the team-vision channel: an enemy ship counts iff
 * it appears in `state.teamVision.get(empire.id ?? empire.teamId)` with
 * `lastSeenSystemId === systemId`. The AI is not given god-mode intel —
 * if it can't see the ship, it can't include it in the threat estimate.
 *
 * "Enemy" = not the same empire AND not a teammate (per `areTeammates`)
 * AND not the synthetic neutral (`EMPIRE_NEUTRAL_ID`). The neutral
 * defensive ships are filtered because AI.md §5.1 strategicValue
 * already accounts for neutral-adjacency signals — counting their
 * defenders here would double-charge the danger term.
 *
 * Defensive against missing ships / missing team-vision: returns 0
 * when intel is unavailable. The dispatcher's `minReadinessRatio` gate
 * will then admit the candidate based on strategicValue alone, which
 * is the intended behaviour for completely-blind exploration.
 */
export function enemyStrength(
  state: GameState,
  empire: Empire,
  systemId: SystemId,
): number {
  const teamMap = state.teamVision.get(empire.id);
  if (!teamMap) return 0;
  let total = 0;
  for (const rec of teamMap.values()) {
    if (rec.lastSeenSystemId !== systemId) continue;
    const ship = state.ships.get(rec.shipId);
    if (!ship) continue;
    // Filter same-empire (defensive — team-vision shouldn't carry these)
    // and teammates per AI.md §5.1.
    if (ship.ownerEmpireId === empire.id) continue;
    if (ship.ownerEmpireId === EMPIRE_NEUTRAL_ID) continue;
    if (areTeammates(state, empire.id, ship.ownerEmpireId)) continue;
    // Skip dead ships — they shouldn't appear in team-vision but the
    // check costs ~1 hull read and avoids a ghost-threat edge case.
    if (ship.currentHull <= 0) continue;
    const ownerEmpire = state.empires.get(ship.ownerEmpireId);
    if (!ownerEmpire) continue;
    total += shipDamagePotential(state, ownerEmpire, ship) * shipHealthFactor(state, ownerEmpire, ship);
  }
  return total;
}

/**
 * Sum of the dispatching fleet's `damagePotential` plus a flat
 * `(hull + shield) × 0.3` survivability bonus per AI.md §5.1.
 *
 * The HP term gives credit for raw durability beyond the ship's
 * firepower — a high-hull tank contributes to fleet readiness even if
 * its own damagePotential is modest. The 0.3 weight is the design-lock
 * value; tunable via xlsx at v4M8.9 if playtest signals otherwise.
 *
 * Pass the candidate dispatching ships (typically the subset filtered
 * by `dispatchCombatJumps` for idleness + chassis class). Empty fleet
 * → 0; gate the dispatch decision on `ownReadiness > 0` upstream.
 */
export function ownReadiness(
  state: GameState,
  empire: Empire,
  ships: readonly ShipInstance[],
): number {
  let total = 0;
  for (const ship of ships) {
    if (ship.currentHull <= 0) continue;
    total += shipDamagePotential(state, empire, ship);
    total += (ship.currentHull + ship.currentShields) * 0.3;
  }
  return total;
}

// ---------------------------------------------------------------------------
// Strategic value
// ---------------------------------------------------------------------------

/** Per-signal contribution weights (AI.md §5.1 hands these as a sum;
 *  the magnitudes are tuned to keep the resulting strategicValue ≤ 1
 *  across the realistic galaxy density range). */
const STRATEGIC_VALUE_WEIGHTS = {
  /** Bonus for each own planet the candidate system threatens. Higher
   *  than the other signals because losing an owned planet is the
   *  largest strategic loss the AI faces. */
  ownPlanetAtRisk: 0.35,
  /** Bonus for each neutral planet adjacent to the candidate system. */
  neutralAdjacent: 0.15,
  /** Bonus when the candidate system is an enemy capital (host of an
   *  enemy empire's first owned planet). Capital systems are
   *  disproportionate strategic targets. */
  enemyCapital: 0.40,
  /** Bonus when at least one teammate has a planet in the candidate
   *  system — joint-staging incentive. */
  teammateAdjacent: 0.10,
};

/**
 * Strategic-value contribution for a candidate target system, in
 * `[0, 1]` per AI.md §5.1.
 *
 * Sums four planet-importance signals (own planets at risk, neutral
 * planets nearby, enemy capital systems, team-mate adjacency) using
 * the weights in `STRATEGIC_VALUE_WEIGHTS`, then clamps to `[0, 1]`.
 *
 * The "own planet at risk" signal counts the empire's own planets in
 * systems adjacent to the candidate — the candidate is treated as a
 * staging system from which the enemy could strike. This is a coarse
 * proxy; v4M8.5 (recall on home-defence) will introduce a separate
 * threat-tier signal that drives recall rather than dispatch.
 *
 * The "enemy capital" signal fires for any system that hosts at least
 * one planet owned by an enemy empire's `planetIds[0]` — the
 * convention used elsewhere in advance-ai (`pickHomeworldPosition`,
 * `homeworldHasDefences`) for "the empire's homeworld."
 */
export function strategicValue(
  state: GameState,
  empire: Empire,
  systemId: SystemId,
): number {
  const system = state.systems.get(systemId);
  if (!system) return 0;

  let score = 0;

  // Own planets at risk — count planets owned by the empire in systems
  // adjacent to the candidate. Each owned planet adjacent contributes
  // one ownPlanetAtRisk weight.
  for (const adjId of system.adjacentSystemIds) {
    const adj = state.systems.get(adjId);
    if (!adj) continue;
    for (const planetId of adj.planetIds) {
      const planet = state.planets.get(planetId);
      if (!planet) continue;
      if (planet.ownerEmpireId === empire.id) {
        score += STRATEGIC_VALUE_WEIGHTS.ownPlanetAtRisk;
      }
    }
  }

  // Neutral planets adjacent — same neighbour walk; planets owned by
  // EMPIRE_NEUTRAL_ID contribute. Uninhabited (ownerEmpireId === null)
  // planets DO NOT contribute — they're worth nothing strategically
  // until colonised.
  for (const adjId of system.adjacentSystemIds) {
    const adj = state.systems.get(adjId);
    if (!adj) continue;
    for (const planetId of adj.planetIds) {
      const planet = state.planets.get(planetId);
      if (!planet) continue;
      if (planet.ownerEmpireId === EMPIRE_NEUTRAL_ID) {
        score += STRATEGIC_VALUE_WEIGHTS.neutralAdjacent;
      }
    }
  }

  // Enemy capital — the candidate system itself hosts a planet that is
  // the first owned planet of an enemy empire (i.e. its homeworld per
  // the advance-ai convention).
  for (const planetId of system.planetIds) {
    const planet = state.planets.get(planetId);
    if (!planet) continue;
    if (planet.ownerEmpireId === null) continue;
    if (planet.ownerEmpireId === empire.id) continue;
    if (planet.ownerEmpireId === EMPIRE_NEUTRAL_ID) continue;
    if (areTeammates(state, empire.id, planet.ownerEmpireId)) continue;
    const enemy = state.empires.get(planet.ownerEmpireId);
    if (!enemy) continue;
    if (enemy.planetIds[0] === planetId) {
      score += STRATEGIC_VALUE_WEIGHTS.enemyCapital;
    }
  }

  // Teammate adjacency — any teammate owns a planet in the candidate
  // system itself (joint-staging signal). Walks the candidate's own
  // planetIds rather than adjacent (different from the own-planet-at-
  // risk signal which walks adjacents).
  for (const planetId of system.planetIds) {
    const planet = state.planets.get(planetId);
    if (!planet) continue;
    if (planet.ownerEmpireId === null) continue;
    if (planet.ownerEmpireId === empire.id) continue;
    if (planet.ownerEmpireId === EMPIRE_NEUTRAL_ID) continue;
    if (areTeammates(state, empire.id, planet.ownerEmpireId)) {
      score += STRATEGIC_VALUE_WEIGHTS.teammateAdjacent;
      // Break to avoid double-counting multiple teammate planets in the
      // same system — the signal is binary "teammate adjacent here".
      break;
    }
  }

  // Clamp to [0, 1] per AI.md §5.1 contract.
  return Math.max(0, Math.min(1, score));
}

// ---------------------------------------------------------------------------
// Composed target score
// ---------------------------------------------------------------------------

/**
 * Inputs for the AI.md §5.1 target-score composition. The caller
 * supplies the per-archetype tunables (typically from
 * `composeEffectiveBehaviour`); this module stays composition-engine
 * free so it can be unit-tested without a full GameState.
 */
export interface TargetScoreInputs {
  /** Per-archetype: multiplies `strategicValue` into the score. */
  strategicWeight: number;
  /** Per-archetype: multiplies the `enemyStrength / ownReadiness` penalty. */
  dangerFactor: number;
  /** Pre-computed (or pre-known) values. Passed as inputs so this fn
   *  stays pure and unit-testable without a GameState. */
  strategicValueComputed: number;
  enemyStrengthComputed: number;
  ownReadinessComputed: number;
}

/**
 * Compose the target-score per AI.md §5.1:
 *
 *   targetScore = strategicValue × strategicWeight
 *                 − dangerFactor × enemyStrength / ownReadiness
 *
 * Guard against divide-by-zero: when `ownReadinessComputed === 0`, the
 * danger term collapses to 0 (no fleet → no engagement → strategic
 * value alone). The dispatcher's `minReadinessRatio` gate handles the
 * "is this fleet ready" question separately; this composition just
 * returns the score so the dispatcher can rank candidates.
 *
 * Returned score is unbounded: higher = more attractive target.
 * Negative values mean the danger term outweighs strategic value — the
 * candidate is "worse than doing nothing". Dispatchers typically apply
 * a `minReadinessRatio` filter BEFORE ranking, but the score itself
 * doesn't require positivity.
 */
export function targetScore(inputs: TargetScoreInputs): number {
  const {
    strategicWeight,
    dangerFactor,
    strategicValueComputed,
    enemyStrengthComputed,
    ownReadinessComputed,
  } = inputs;
  const dangerTerm =
    ownReadinessComputed > 0
      ? (dangerFactor * enemyStrengthComputed) / ownReadinessComputed
      : 0;
  return strategicValueComputed * strategicWeight - dangerTerm;
}

// ---------------------------------------------------------------------------
// Re-exports for downstream tests / consumers
// ---------------------------------------------------------------------------

/** Exposed for v4M8.2a tests + future tuning. Treat as authored constants. */
export { STRATEGIC_VALUE_WEIGHTS };

/** Re-exported planet shape so consumers don't need to re-import from
 *  state/types alongside the targeting helpers. */
export type { Planet };

// ---------------------------------------------------------------------------
// v4M8.2b — pickCombatJumpDestinationThreatWeighted
// ---------------------------------------------------------------------------

/**
 * Result shape for {@link pickCombatJumpDestinationThreatWeighted}.
 *
 * `destSystemId` is the final destination the dispatcher is steering
 * toward (the candidate that won the targetScore ranking).
 * `firstStep` is the system to jump to next — either `destSystemId`
 * itself (direct in-range hop) or an intermediate stepping-stone
 * resolved via `firstStepTowards` per AI.md §5.2.
 * `score` is the composed targetScore (positive = attractive,
 * negative = "worse than doing nothing"). Returned so consumers can
 * surface it on the dispatch order for debugging / playtest signal.
 */
export interface ThreatWeightedDestination {
  destSystemId: SystemId;
  firstStep: SystemId;
  score: number;
}

/**
 * v4M8.2b — threat-weighted combat-jump destination picker per
 * AI.md §5.1 (target evaluation) and §5.2 (multi-hop pathing).
 *
 * Walks every system in the galaxy, scores each as a target via
 * {@link targetScore}, and returns the highest-scoring candidate that
 * passes both the multi-hop range filter (via `firstStepTowards`) and
 * the per-archetype `minReadinessRatio` gate.
 *
 * **Caller responsibilities** (kept out of this function for clean
 * separation of concerns between targeting and composition):
 *   - Compose the `behaviour` via `composeEffectiveBehaviour(state, empire)`.
 *   - Pre-compute the dispatching fleet's effective range
 *     (`fleetRange = min over fleet ships of (template.hyperdriveRange ×
 *     empireMul)`). Same formula `applyJumpShips` enforces.
 *   - Supply the `dispatchFleet` ship list (typically the candidates
 *     produced by the upstream readiness filter — idle, in-system,
 *     non-Scout, non-Constructor, non-Coloniser, non-cloaked).
 *
 * Returns `null` when:
 *   - No source system (defensive against missing `sourceSystemId`).
 *   - `fleetRange <= 0` or non-finite.
 *   - `dispatchFleet` is empty (no ships to dispatch → no readiness).
 *   - No candidate passes the `minReadinessRatio` gate.
 *
 * Pure — no state mutation, no RNG draws. Determinism: candidate
 * iteration follows `state.systems` insertion order; ties broken by
 * ascending candidate id so cold-start (every system worth zero)
 * resolves to the lowest-id reachable system.
 *
 * v4M8.2b status: NOT yet consumed by dispatch. The legacy
 * `pickCombatJumpDestination` in `advance-ai.ts` remains the active
 * path; this function lives in parallel for the strict-parity posture
 * the user locked at the v4M8.2 opener. Consumer flip is a later
 * v4M8.x checkpoint after .2a/.2b/.2c land + bake.
 */
export function pickCombatJumpDestinationThreatWeighted(
  state: GameState,
  empire: Empire,
  sourceSystemId: SystemId,
  dispatchFleet: readonly ShipInstance[],
  behaviour: EffectiveBehaviour,
  fleetRange: number,
  /**
   * v4M8.3 — optional A* pathing scalars. When both are positive AND
   * `behaviour.pathFuelWeight > 0`, the picker routes via
   * `firstStepTowardsWeighted` (Dijkstra with edge weight `hopDist ×
   * (fuelCostPerUnit + α × travelTimePerUnit)` per AI.md §5.2). When
   * either is 0 (the default for callers that haven't migrated), the
   * picker falls back to `firstStepTowards` BFS — preserves the
   * v4M8.2b behaviour for in-flight call sites and tests.
   */
  fuelCostPerUnit: number = 0,
  travelTimePerUnit: number = 0,
): ThreatWeightedDestination | null {
  if (!state.systems.has(sourceSystemId)) return null;
  if (!Number.isFinite(fleetRange) || fleetRange <= 0) return null;
  if (dispatchFleet.length === 0) return null;

  // v4M8.3 — A* path selection when caller supplied fuel/speed scalars
  // and the archetype's pathFuelWeight is non-zero. Otherwise BFS via
  // the legacy v4M5.5 helper.
  const useWeightedPath =
    behaviour.pathFuelWeight > 0 &&
    fuelCostPerUnit > 0 &&
    travelTimePerUnit > 0;

  // Pre-compute ownReadiness once — it doesn't depend on the candidate.
  const ownReadinessComputed = ownReadiness(state, empire, dispatchFleet);

  let best: {
    destSystemId: SystemId;
    firstStep: SystemId;
    score: number;
  } | null = null;

  for (const candidate of state.systems.values()) {
    if (candidate.id === sourceSystemId) continue;

    // Range filter via multi-hop. `firstStep` is either the target
    // (direct hop) or the first stepping stone (multi-hop). Path-
    // finder choice is archetype-driven per v4M8.3 wiring above.
    const firstStep = useWeightedPath
      ? firstStepTowardsWeighted(
          state,
          sourceSystemId,
          candidate.id,
          fleetRange,
          fuelCostPerUnit,
          travelTimePerUnit,
          behaviour.pathFuelWeight,
        )
      : firstStepTowards(state, sourceSystemId, candidate.id, fleetRange);
    if (firstStep === null) continue;

    const enemyStrengthComputed = enemyStrength(state, empire, candidate.id);
    const strategicValueComputed = strategicValue(state, empire, candidate.id);

    // minReadinessRatio gate per AI.md §5.1. When enemyStrength is 0
    // (no visible threat) the gate is trivially satisfied — undefended
    // systems are always engageable.
    if (enemyStrengthComputed > 0) {
      const ratio = ownReadinessComputed / enemyStrengthComputed;
      if (ratio < behaviour.minReadinessRatio) continue;
    }

    let score = targetScore({
      strategicWeight: behaviour.strategicWeight,
      dangerFactor: behaviour.dangerFactor,
      strategicValueComputed,
      enemyStrengthComputed,
      ownReadinessComputed,
    });

    // v4M8.7c — team-aware coordinated dispatch penalty per AI.md §5.8.
    // Subtract `teammatePenalty × count(teammate claims on firstStep)`
    // so teammates spread task forces across different next-hops
    // instead of converging on the same system. Penalty looked up on
    // `firstStep` (the actual moveAndJumpShips target this dispatch
    // would issue), matching what v4M8.7b emits — symmetric with the
    // claim emission semantics. Solo empires (teamId === null) get 0
    // back from `teammateClaimCountForSystem` — no behaviour change.
    if (behaviour.teammatePenalty > 0) {
      const teammateClaims = teammateClaimCountForSystem(
        state,
        empire,
        firstStep,
      );
      score -= behaviour.teammatePenalty * teammateClaims;
    }

    // v4M13.4d — AI hazard-awareness per `TERRAIN_FEATURES.md §5.2`.
    // Subtract the candidate destination system's hazard penalty
    // (sum of `AI_HAZARD_PENALTY` per environment in
    // `balance-environment.ts`) from the target score so AI avoids
    // routing combat task forces through black holes / radiation
    // belts / ion clouds. Nebula + exotic-gas-cloud contribute 0
    // (nebula is a defensive asset; exotic-gas-cloud is benign).
    // Penalty applied to the FINAL destination (`candidate.id`),
    // not just `firstStep` — combat dispatch chooses where the
    // task force ultimately lands.
    const hazardPenalty = aiHazardPenaltyForSystem(state, candidate.id);
    if (hazardPenalty > 0) {
      score -= hazardPenalty;
    }

    // Pick highest score; tie-break on ascending candidate id so the
    // cold-start "every candidate scores 0" case picks the lowest-id
    // reachable system deterministically (same convention as the
    // legacy `pickCombatJumpDestination`).
    if (
      best === null ||
      score > best.score ||
      (score === best.score && candidate.id < best.destSystemId)
    ) {
      best = { destSystemId: candidate.id, firstStep, score };
    }
  }

  return best;
}
